import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Overline } from "./anim";

export default function Founder({ onBook }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);
  const ySmall = useTransform(scrollYProgress, [0, 1], ["6%", "-6%"]);

  return (
    <section id="founder" data-testid="founder-section" className="mx-auto max-w-7xl overflow-visible px-5 py-28 sm:px-8 sm:py-36">
      <div ref={ref} className="grid items-center gap-16 lg:grid-cols-2 lg:gap-24">
        <div className="relative mx-auto w-full max-w-md lg:mx-0">
          <Reveal>
            <div className="relative">
              <span className="absolute -left-4 -top-4 h-full w-full border border-[#C5A059]/35" aria-hidden="true" />
              <motion.div style={{ y: yMain }} className="relative overflow-hidden">
                <img
                  src="/assets/founder-editorial.png"
                  alt="Founder of Kingdom Wealth Enterprises, editorial portrait"
                  data-testid="founder-portrait"
                  className="aspect-[4/5] w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08080A]/45 via-transparent to-transparent" />
              </motion.div>
              <motion.div
                style={{ y: ySmall }}
                className="absolute -bottom-10 -right-6 hidden w-44 overflow-hidden border-4 border-[#08080A] shadow-2xl shadow-black/60 sm:block lg:-right-12"
              >
                <img
                  src="/assets/founder-consult.png"
                  alt="Founder reviewing strategy with a client"
                  className="aspect-square w-full object-cover"
                  loading="lazy"
                />
              </motion.div>
            </div>
          </Reveal>
        </div>

        <div className="pt-6 lg:pt-0">
          <Reveal>
            <Overline>Meet the Founder</Overline>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 text-3xl font-light leading-[1.12] tracking-tight text-[#F9F8F6] sm:text-4xl">
              Jenn <span className="text-[#C5A059]">Woodward</span>
            </h2>
            <p className="mt-2 text-xs font-semibold uppercase tracking-[0.28em] text-[#A1A1AA]">
              Business Strategist, Entrepreneur, Investor
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-lg text-base font-light leading-relaxed text-[#C9C9CF]">
              Jenn brings more than 20 years of experience in operations management, business development, and
              wealth-building. Her entrepreneurial journey began at 15, and at 17 her employer created a
              leadership role designed specifically around her strengths, a pattern that repeated throughout her
              career.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <p className="mt-4 max-w-lg text-base font-light leading-relaxed text-[#A1A1AA]">
              Across retail, technology, construction, and real estate, she has built businesses, managed
              operations, and pursued her own investments, often while balancing multiple jobs and raising her
              child. That hands-on experience is what she now brings to every client.
            </p>
          </Reveal>
          <Reveal delay={0.32}>
            <p className="mt-4 max-w-lg border-l-2 border-[#C5A059]/60 pl-5 text-base font-light italic leading-relaxed text-[#F9F8F6]">
              Her mission is simple: give clients the strategic perspective, experience, and support they need to
              make calculated decisions, recognize opportunities, and build wealth with confidence.
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="founder-milestones">
              {[
                ["15", "Entered the workforce"],
                ["17", "Leadership role created for her"],
                ["20+", "Years building & investing"],
                ["Today", "Founder, Kingdom Wealth"],
              ].map(([num, label]) => (
                <div
                  key={label}
                  className="group border border-white/10 bg-[#0F0F12] p-4 transition-colors duration-500 hover:border-[#C5A059]/50"
                >
                  <p className="text-2xl font-light text-[#C5A059]">{num}</p>
                  <p className="mt-1.5 text-[10px] font-medium uppercase leading-snug tracking-[0.14em] text-[#71717A] transition-colors duration-500 group-hover:text-[#A1A1AA]">
                    {label}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.32}>
            <div className="mt-8 flex items-center gap-4 border-t border-white/10 pt-6">
              <img
                src="/assets/founder-editorial.png"
                alt="Jenn Woodward, founder of Kingdom Wealth Enterprises"
                className="h-12 w-12 rounded-full border border-[#C5A059]/40 object-cover object-top"
                loading="lazy"
              />
              <div>
                <p className="text-sm font-medium text-[#F9F8F6]">Jenn Woodward</p>
                <p className="mt-0.5 text-xs uppercase tracking-[0.2em] text-[#71717A]">Founder, Kingdom Wealth Enterprises, LLC</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <button data-testid="founder-book-cta" onClick={onBook} className="btn-ghost mt-8">
              Schedule a Consultation
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
